import { useCallback } from 'react'
import {
  useIsMutating,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'
import {
  bulkUploadBillingProfiles,
  createBillingProfile,
  getAllBillingProfiles,
  getBillingProfileActivityLog,
  getBillingProfileStats,
  getBillingProfiles,
  runHubspotSync,
  submitHubspotSync,
  toggleBillingProfileStatus,
  updateBillingProfile,
} from './client-billing-profile.api'
import {
  BillingProfileRecord,
  BillingProfileStatsDto,
  BulkUploadResultDto,
  CreateBillingProfileRequestDto,
  GetBillingProfilesRequestDto,
  GetBillingProfilesResponseDto,
  GetBillingProfileStatsRequestDto,
  HubspotSyncResultDto,
  HubspotSyncSubmitDto,
  HubspotSyncSubmitResultDto,
  ToggleBillingProfileStatusRequestDto,
  UpdateBillingProfileRequestDto,
} from './dto/client-billing-profile.dto'
import {
  GetActivityLogResponseDto,
  GetBillingProfileActivityLogRequestDto,
} from './dto/activity-log.dto'
import { INVOICE_DELIVERY_QUERY_KEY } from './invoice-delivery-api-client'
// The user list is derived from profile recipients, so every write here can
// change who appears on the User tab.
import { QUERY_KEY as USERS_QUERY_KEY } from './user-invitation-api-client'

const QUERY_KEY = 'billing-profile'

export const useBillingProfilesQuery = (
  params: GetBillingProfilesRequestDto = {},
  options?: Omit<
    UseQueryOptions<
      GetBillingProfilesResponseDto,
      Error,
      GetBillingProfilesResponseDto
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    GetBillingProfilesResponseDto,
    Error,
    GetBillingProfilesResponseDto
  >({
    queryKey: [QUERY_KEY, params],
    queryFn: () => getBillingProfiles(params),
    ...options,
  })
}

/**
 * Every profile, paged through in one go. Test clients are included: this backs
 * comparisons against stored values, not a list an operator reads.
 */
export const useAllBillingProfilesQuery = (
  options?: Omit<
    UseQueryOptions<BillingProfileRecord[], Error, BillingProfileRecord[]>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<BillingProfileRecord[], Error, BillingProfileRecord[]>({
    queryKey: [QUERY_KEY, 'all'],
    queryFn: () => getAllBillingProfiles(),
    ...options,
  })
}

export const useBillingProfileStatsQuery = (
  params: GetBillingProfileStatsRequestDto = {},
  options?: Omit<
    UseQueryOptions<BillingProfileStatsDto, Error, BillingProfileStatsDto>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<BillingProfileStatsDto, Error, BillingProfileStatsDto>({
    queryKey: [QUERY_KEY, 'stats', params],
    queryFn: () => getBillingProfileStats(params),
    ...options,
  })
}

export const useBillingProfileActivityLogQuery = (
  params: GetBillingProfileActivityLogRequestDto,
  options?: Omit<
    UseQueryOptions<
      GetActivityLogResponseDto,
      Error,
      GetActivityLogResponseDto
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<GetActivityLogResponseDto, Error, GetActivityLogResponseDto>({
    queryKey: [QUERY_KEY, 'activity-log', params],
    queryFn: () => getBillingProfileActivityLog(params),
    enabled: true,
    ...options,
  })
}

export const useCreateBillingProfileMutation = (
  options?: UseMutationOptions<
    BillingProfileRecord,
    Error,
    CreateBillingProfileRequestDto
  >
) => {
  const queryClient = useQueryClient()
  return useMutation<
    BillingProfileRecord,
    Error,
    CreateBillingProfileRequestDto
  >({
    ...options,
    mutationFn: createBillingProfile,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [INVOICE_DELIVERY_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}

export const useUpdateBillingProfileMutation = (
  options?: UseMutationOptions<
    BillingProfileRecord,
    Error,
    { id: number; body: UpdateBillingProfileRequestDto }
  >
) => {
  const queryClient = useQueryClient()
  return useMutation<
    BillingProfileRecord,
    Error,
    { id: number; body: UpdateBillingProfileRequestDto }
  >({
    ...options,
    mutationFn: ({ id, body }) => updateBillingProfile(id, body),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [INVOICE_DELIVERY_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}

/*
 * Resolves once the profile list the update mutation invalidated has reloaded.
 * cancelRefetch: false joins that in-flight request instead of firing another.
 */
export const useWaitForBillingProfilesRefresh = () => {
  const queryClient = useQueryClient()
  return useCallback(
    () =>
      queryClient.refetchQueries(
        { queryKey: [QUERY_KEY], type: 'active' },
        { cancelRefetch: false }
      ),
    [queryClient]
  )
}

export const useToggleBillingProfileStatusMutation = (
  options?: UseMutationOptions<
    BillingProfileRecord,
    Error,
    { id: number; body: ToggleBillingProfileStatusRequestDto }
  >
) => {
  const queryClient = useQueryClient()
  return useMutation<
    BillingProfileRecord,
    Error,
    { id: number; body: ToggleBillingProfileStatusRequestDto }
  >({
    ...options,
    mutationFn: ({ id, body }) => toggleBillingProfileStatus(id, body),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [INVOICE_DELIVERY_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}

export const useBulkUploadBillingProfilesMutation = (
  options?: UseMutationOptions<BulkUploadResultDto, Error, File>
) => {
  const queryClient = useQueryClient()
  return useMutation<BulkUploadResultDto, Error, File>({
    ...options,
    mutationFn: bulkUploadBillingProfiles,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [INVOICE_DELIVERY_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}

type HubspotSyncRunVariables = {
  force?: boolean
  excludeTestClients?: boolean
}

export const useHubspotSyncRunMutation = (
  options?: UseMutationOptions<
    HubspotSyncResultDto,
    Error,
    HubspotSyncRunVariables | void
  >
) => {
  const queryClient = useQueryClient()
  return useMutation<
    HubspotSyncResultDto,
    Error,
    HubspotSyncRunVariables | void
  >({
    ...options,
    mutationFn: (variables) =>
      runHubspotSync(
        variables?.excludeTestClients ?? true,
        variables?.force ?? false
      ),
    // A run only previews; the submit below is what writes.
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [INVOICE_DELIVERY_QUERY_KEY] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}

const HUBSPOT_SYNC_MUTATION_KEY = [QUERY_KEY, 'hubspot-sync-submit']

/**
 * True while any HubSpot sync write is in flight, from whichever panel. Each
 * write sends a profile's full recipient list, so two at once can undo each
 * other.
 */
export const useIsHubspotSyncing = (): boolean =>
  useIsMutating({ mutationKey: HUBSPOT_SYNC_MUTATION_KEY }) > 0

export const useHubspotSyncMutation = (
  options?: UseMutationOptions<
    HubspotSyncSubmitResultDto,
    Error,
    HubspotSyncSubmitDto
  >
) => {
  const queryClient = useQueryClient()
  return useMutation<HubspotSyncSubmitResultDto, Error, HubspotSyncSubmitDto>({
    ...options,
    mutationKey: HUBSPOT_SYNC_MUTATION_KEY,
    mutationFn: submitHubspotSync,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [INVOICE_DELIVERY_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}
